import React, {forwardRef, useRef, useImperativeHandle} from "react";

const KEY_CODES = {
    PAGE_UP: 33,
    PAGE_DOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    ENTER: 13,
};
export const TAB_GROUP_FOCUS_CLASS = "ch-tg-f";
const SELECTABLE_INTERNAL_COMPONENTS = "a, button, input, select, textarea, [role='button']";

const TabGroupWrap = forwardRef((props, ref) => {
    const {
        targetRef,
        rowSelector,
        cellSelector,
        cellActive,
        containerSelector,
        wrap = true,
        disabled = false,
        onFocusCellChange,
        children
    } = props;
    const baseRef = useRef();
    const updatingRef = useRef(false);
    const currentPosDefault = {rowIndex: 0, colIndex: 0, subIndex: 0};
    const currentPosRef = useRef(currentPosDefault);
    const cleanupRef = useRef();

    useImperativeHandle(ref, () => ({
        build: () => {
            setupFocusManager();
        }
    }));

    const setupFocusManager = () => {
        if (cleanupRef.current) cleanupRef.current();

        // const baseTarget = targetRef.current?.querySelector(".ant-table-content");
        const baseTarget = containerSelector
            ? targetRef.current?.querySelector(containerSelector)
            : targetRef.current;

        if (!baseTarget) return;
        baseRef.current = baseTarget;

        const matrix = getMatrixInfo();

        let startCell = null;
        let startPos = {...currentPosDefault};

        if (cellActive) {
            matrix.cells.forEach((row, rIdx) => {
                row.forEach((cell, cIdx) => {
                    if (!startCell && cell.classList.contains(cellActive)) {
                        startCell = cell;
                        startPos = {rowIndex: rIdx, colIndex: cIdx, subIndex: 0};
                    }
                });
            });
        }

        if (!startCell) {
            const memoryCell = matrix.cells[currentPosRef.current.rowIndex]?.[currentPosRef.current.colIndex];
            if (memoryCell) {
                startCell = memoryCell;
                startPos = currentPosRef.current;
            }
        }

        if (!startCell) {
            startCell = matrix.cells[0]?.[0];
            startPos = currentPosDefault;
        }

        currentPosRef.current = startPos;

        requestAnimationFrame(() => {
            const freshMatrix = getMatrixInfo();
            focusCell(startCell, freshMatrix, false);
        });

        const observer = new MutationObserver(() => {
            requestAnimationFrame(() => {
                const tabbableEls = baseRef.current?.querySelectorAll('[tabindex]:not([tabindex="-1"])');
                if (tabbableEls.length === 0 && !updatingRef.current) {
                    currentPosRef.current = currentPosDefault;
                    const matrix = getMatrixInfo();
                    focusCell(matrix.currentCell, matrix, false);
                }
            });
        });

        baseRef.current.addEventListener("click", handleClick);
        baseRef.current.addEventListener("keydown", handleKeyDown);
        observer.observe(baseTarget, {childList: true, subtree: true});

        cleanupRef.current = () => {
            baseRef.current?.removeEventListener("click", handleClick);
            baseRef.current?.removeEventListener("keydown", handleKeyDown);
            observer.disconnect();
        };
    }

    const clearTabIndex = () => {
        const focusedCells = baseRef.current?.querySelectorAll(`${cellSelector}[tabindex="0"], ${SELECTABLE_INTERNAL_COMPONENTS}`);
        if (!focusedCells || focusedCells.length === 0) return;

        focusedCells.forEach(cell => {
            if (cell.matches(SELECTABLE_INTERNAL_COMPONENTS) && cell.tabIndex !== -1) {
                cell.setAttribute("tabindex", "-1");
            } else if (cell.tabIndex !== -1) {
                cell.removeAttribute("tabindex");
                cell.removeAttribute("focusable");
            }
        });
    };

    const handleClick = event => {
        if (!event || disabled) return;
        if (event.target.matches(SELECTABLE_INTERNAL_COMPONENTS)) return;
        const newCell = getCellFromTarget(event.target);
        const matrix = getMatrixInfo();
        focusCell(newCell, matrix, true);
    }

    const handleKeyDown = event => {
        if (!event || disabled) return;

        let key = event.which || event.keyCode;
        let nextCell;
        const matrix = getMatrixInfo();

        const isVertical = key === KEY_CODES.UP || key === KEY_CODES.DOWN;
        const isForward = key === KEY_CODES.RIGHT || key === KEY_CODES.DOWN;
        const dir = isForward ? 1 : -1;

        switch (key) {
            case KEY_CODES.UP:
            case KEY_CODES.DOWN:
            case KEY_CODES.LEFT:
            case KEY_CODES.RIGHT:
                const internalComponents = matrix.currentCell.querySelectorAll(SELECTABLE_INTERNAL_COMPONENTS);
                if (internalComponents.length > 1) {
                    const newSubIndex = (currentPosRef.current.subIndex || 0) + dir;
                    const inBounds = newSubIndex >= 0 && newSubIndex < internalComponents.length;
                    if (inBounds) {
                        currentPosRef.current.subIndex = newSubIndex;
                        focusCell(matrix.currentCell, matrix, true);
                        event.preventDefault();
                        return;
                    }
                }
                if (rowSelector == null) {
                    nextCell = getNextVisibleCell(matrix, dir, 0);
                } else {
                    if (isVertical) {
                        nextCell = getNextVisibleCell(matrix, 0, dir);
                    } else {
                        nextCell = getNextVisibleCell(matrix, dir, 0);
                    }
                }
                break;
            case KEY_CODES.PAGE_DOWN:
                nextCell = getNextVisibleCell(matrix, 0, 5);
                break;
            case KEY_CODES.PAGE_UP:
                nextCell = getNextVisibleCell(matrix, 0, -5);
                break;
            case KEY_CODES.HOME:
                if (event.ctrlKey) {
                    nextCell = matrix.cells?.[0]?.[0];
                } else {
                    nextCell = matrix.cells?.[matrix.rowIndex]?.[0];
                }
                break;
            case KEY_CODES.END:
                if (event.ctrlKey) {
                    const lastRow = matrix.cells?.[matrix.cells.length - 1];
                    nextCell = lastRow?.[lastRow.length - 1];
                } else {
                    const row = matrix.cells?.[matrix.rowIndex];
                    nextCell = row?.[row.length - 1];
                }
                break;
            case KEY_CODES.ENTER:
                if (event.target.tagName === "A" && !event.target.hasAttribute("href")) {
                    event.preventDefault();
                    event.target.dispatchEvent(
                        new MouseEvent("click", { bubbles: true, cancelable: true })
                    );
                    return;
                }
                break;
            default:
                return;
        }

        if (nextCell) {
            focusCell(nextCell, matrix, true);
            event.preventDefault();
        }
    }

    const getNextVisibleCell = (matrix, directionX, directionY) => {
        let row = matrix.rowIndex + directionY;
        let col = matrix.colIndex + directionX;

        if (col >= matrix.cells[matrix.rowIndex].length) {
            if (matrix.rowIndex < matrix.cells.length - 1) {
                row = matrix.rowIndex + 1;
                col = 0;
            } else if (wrap) {
                row = 0; col = 0;
            }
        } else if (col < 0) {
            if (matrix.rowIndex > 0) {
                row = matrix.rowIndex - 1;
                col = matrix.cells[row].length - 1;
            } else if (wrap) {
                row = matrix.cells.length - 1;
                col = matrix.cells[row].length - 1;
            }
        }

        row = Math.max(0, Math.min(row, matrix.cells.length - 1));
        const rowCells = matrix.cells[row] || [];
        col = Math.max(0, Math.min(col, rowCells.length - 1));

        return matrix.cells[row][col];
    };

    const getMatrixInfo = () => {
        const {rowIndex, colIndex} = currentPosRef.current;
        const rows = !rowSelector ? [baseRef.current] : Array.from(baseRef.current?.querySelectorAll(rowSelector));
        const cells = rows.map(row => {
            const matches = Array.from(row.querySelectorAll(cellSelector));
            return matches.filter(el =>
                !matches.some(parent => parent !== el && parent.contains(el))
            );
        });
        const currentRow = rows[rowIndex];
        const currentCell = cells[rowIndex]?.[colIndex];

        return {
            rows,
            cells,
            rowIndex,
            colIndex,
            currentCell,
            currentRow
        }
    }

    const getCellFromTarget = (target) => {
        const cell = target.closest(cellSelector);
        const withinRow = rowSelector ? target.closest(rowSelector) : baseRef.current;
        return withinRow && cell ? cell : null;
    }

    const getFocusableElement = (cell, subIndex = 0) => {
        if (!cell) return null;

        const isSelfFocusable = cell.matches(SELECTABLE_INTERNAL_COMPONENTS);
        const internals = cell.querySelectorAll(SELECTABLE_INTERNAL_COMPONENTS);

        if (internals.length > 0) return internals[subIndex];
        if (isSelfFocusable) return cell;
        return cell;
    };

    const focusCell = (cell, matrix, updateFocus = true) => {
        const updateCurrentCell = (cell, matrix, callback) => {
            const newRowIndex = matrix.rows.findIndex(row => row.contains(cell));
            if (newRowIndex === -1) return;
            const newColIndex = matrix.cells[newRowIndex].findIndex(c => c === cell || c.contains(cell));
            if (newColIndex === -1) return;

            let newSubIndex = 0;
            const cellLeft = matrix.cells[newRowIndex][newColIndex + 1];
            if (cellLeft && cellLeft === matrix.currentCell) {
                const nextInternalComponents = cell.querySelectorAll(SELECTABLE_INTERNAL_COMPONENTS);
                newSubIndex = Math.max(0, nextInternalComponents.length - 1);
            }

            currentPosRef.current = {rowIndex: newRowIndex, colIndex: newColIndex, subIndex: newSubIndex};

            if (onFocusCellChange && typeof onFocusCellChange === "function") {
                updatingRef.current = true;
                onFocusCellChange(currentPosRef.current);
                requestAnimationFrame(() => {
                    updatingRef.current = false;
                });
            }

            requestAnimationFrame(()=> {
                if (callback && typeof callback === "function") callback();
            });
        }
        const applyFocus = () => {
            const freshMatrix = getMatrixInfo();
            const finalTarget = getFocusableElement(
                freshMatrix.currentCell,
                currentPosRef.current.subIndex
            );

            if (!finalTarget) return;

            clearTabIndex();

            finalTarget.setAttribute("tabindex", "0");

            if (finalTarget.namespaceURI === "http://www.w3.org/2000/svg") {
                finalTarget.setAttribute("focusable", "true");
            }
            // freshFinalTarget.classList.add(TAB_GROUP_FOCUS_CLASS);
            if (updateFocus) finalTarget.focus();
        }

        if (matrix.currentCell !== cell) {
            updateCurrentCell(cell, matrix, () => applyFocus());
        } else {
            applyFocus();
        }
    }

    return (
        <div ref={ref}>{children}</div>
    )
});

export default TabGroupWrap;